import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Check, Sparkles, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function Pricing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(null);

  useEffect(() => {
    if (user === false) { navigate("/login"); return; }
    if (user) api.get("/subscription/status").then((r) => setStatus(r.data)).catch(() => {});
  }, [user, navigate]);

  async function pick(plan) {
    setLoadingPlan(plan);
    try {
      const { data } = await api.post("/subscription/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(apiError(e)); setLoadingPlan(null);
    }
  }

  const isActive = status && (status.status === "active" || status.status === "lifetime");
  const trialLeft = status?.status === "trial" ? status.days_left : null;

  return (
    <div className="min-h-screen bg-[#FAF9F6] py-16 px-6 relative">
      <div className="absolute top-6 right-6"><LanguageSwitcher /></div>
      <button onClick={() => navigate(-1)} data-testid="back-btn" className="absolute top-6 left-6 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-stone-900">
        <ArrowLeft className="w-3 h-3" /> {t("common.back")}
      </button>
      <div className="max-w-5xl mx-auto fade-in-up">
        <div className="text-center mb-14">
          <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mb-3">{t("pricing.eyebrow")}</p>
          <h1 className="font-heading text-5xl lg:text-6xl font-light tracking-tight">{t("pricing.title")}</h1>
          <p className="text-stone-600 mt-4 max-w-xl mx-auto">{t("pricing.subtitle")}</p>
          {trialLeft !== null && trialLeft > 0 && (
            <div className="mt-6 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#D4A373]/15 text-[#B07D4F] text-sm">
              <Sparkles className="w-4 h-4" /> {t("pricing.trialLeft", { days: trialLeft })}
            </div>
          )}
          {isActive && (
            <div className="mt-6 inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#8FA08B]/15 text-[#4A6249] text-sm">
              <Check className="w-4 h-4" /> {t("pricing.alreadyActive")}
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <PlanCard plan="monthly" price={29} period={t("pricing.perMonth")} title={t("pricing.monthlyTitle")}
            desc={t("pricing.monthlyDesc")} onPick={pick} loading={loadingPlan === "monthly"} testId="plan-monthly" />
          <PlanCard plan="yearly" price={290} period={t("pricing.perYear")} title={t("pricing.yearlyTitle")}
            desc={t("pricing.yearlyDesc")} highlight badge={t("pricing.savings")} onPick={pick} loading={loadingPlan === "yearly"} testId="plan-yearly" />
        </div>

        <div className="mt-14 max-w-2xl mx-auto text-center text-xs text-stone-500 space-y-2">
          <p>{t("pricing.methods")}</p>
          <p>{t("pricing.cancelAnytime")}</p>
        </div>
      </div>
    </div>
  );
}

const FEATURES_KEYS = ["featurePatients", "featureCalendar", "featureConsent", "featureInvoices", "featurePwa", "featureSupport"];

function PlanCard({ plan, price, period, title, desc, highlight, badge, onPick, loading, testId }) {
  const { t } = useTranslation();
  return (
    <div data-testid={testId} className={`relative rounded-3xl p-8 border transition-all ${
      highlight
        ? "bg-[#1C1917] text-white border-[#1C1917] shadow-xl md:-translate-y-2"
        : "bg-white border-stone-200 hover:border-[#D4A373]"
    }`}>
      {highlight && badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-[#D4A373] text-stone-900 text-xs uppercase tracking-widest">{badge}</div>
      )}
      <p className={`text-xs uppercase tracking-[0.3em] mb-3 ${highlight ? "text-stone-400" : "text-stone-500"}`}>{title}</p>
      <div className="flex items-baseline gap-2 mb-5">
        <span className="font-heading text-5xl font-light">€{price}</span>
        <span className={`text-sm ${highlight ? "text-stone-400" : "text-stone-500"}`}>{period}</span>
      </div>
      <p className={`text-sm mb-6 ${highlight ? "text-stone-300" : "text-stone-600"}`}>{desc}</p>
      <ul className="space-y-2.5 mb-8">
        {FEATURES_KEYS.map((k) => (
          <li key={k} className={`flex items-start gap-2 text-sm ${highlight ? "text-stone-200" : "text-stone-700"}`}>
            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${highlight ? "text-[#D4A373]" : "text-[#8FA08B]"}`} />
            <span>{t(`pricing.${k}`)}</span>
          </li>
        ))}
      </ul>
      <button onClick={() => onPick(plan)} disabled={loading} data-testid={`pick-${plan}`}
        className={`w-full py-3 rounded-full font-medium transition-colors disabled:opacity-60 ${
          highlight
            ? "bg-[#D4A373] text-stone-900 hover:bg-white"
            : "bg-stone-900 text-white hover:bg-stone-800"
        }`}>
        {loading ? t("pricing.redirecting") : t("pricing.selectPlan")}
      </button>
    </div>
  );
}
